import { Upload01 } from "@untitledui/icons";
import { useRef } from "react";
import { Button } from "@/components/base/buttons/button";
import { ImportCsvDrawer } from "./import-csv-drawer";
import type { ImportCSVProps, ImportCSVRow } from "./types";
import { useImportCsvState } from "./use-import-csv-state";

export type { ImportCSVProps, ImportCSVRow, SaveResult } from "./types";

export const ImportCSV = <T extends ImportCSVRow, TSummary>({
  triggerColor = "secondary",
  renderTrigger,
  className,
  renderRowExtra,
  columns,
  onDownloadTemplate,
  onPreview,
  onChange,
  onSave,
  getSuccessMessage,
  actionType,
}: ImportCSVProps<T, TSummary>) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const state = useImportCsvState<T, TSummary>({ onPreview, onSave, getSuccessMessage, actionType, onChange });

  const handleFileChange = async (file: File | undefined) => {
    if (!file) return;
    try {
      await state.handleFileSelected(file);
    } finally {
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const openFilePicker = () => inputRef.current?.click();

  return (
    <>
      {renderTrigger ? (
        renderTrigger(openFilePicker, state.isPreviewing)
      ) : (
        <Button color={triggerColor} iconLeading={Upload01} isLoading={state.isPreviewing} onClick={openFilePicker}>
          Import CSV
        </Button>
      )}
      <input
        ref={inputRef}
        type="file"
        accept=".csv,text/csv"
        className="hidden"
        onChange={(e) => handleFileChange(e.target.files?.[0])}
      />

      {state.open && (
        <ImportCsvDrawer<T>
          drawerState={state.drawerState}
          rows={state.rows}
          selected={state.selected}
          isSaving={state.isSaving}
          renderRowExtra={renderRowExtra}
          columns={columns}
          className={className}
          onDownloadTemplate={onDownloadTemplate}
          onToggleRow={state.toggleRow}
          onToggleAll={state.toggleAll}
          onRequestClose={state.requestClose}
          onKeepReviewing={state.keepReviewing}
          onDiscard={state.discard}
          onSave={state.save}
        />
      )}
    </>
  );
};
